/**
 * File Name          : packet.model.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { Uint16Bytes } from "../../byte-helpers";
import { DATA_TYPE } from "../data-type.model";

export class Packet {
    mBytes: Uint8Array | null = null;

    //Header values
    mPayloadLength: number | null = null;
    mPacketNumber: number | null = null;
    mPacketType: PACKET_TYPE | null = null;
    pktSubType: number | null = null;
    rxCRCBytes: Uint8Array | null = null;
    rxCRC: number | null = null;
    validCRC: boolean | null = null;
    calcCRC: number | null = null;
    payloadBytes: Uint8Array | null = null;

    static crcTable: Array<number | null> = Packet.buildCRCTable(0xBAAD);

    static fromBytes(bytes: Uint8Array): Packet {
        var pkt = new Packet()
        pkt.mBytes = bytes
        pkt.parseHeader()

        return pkt
    }

    static buildCRCTable(polynomial: number): Array<number | null> {
        const retTable: Array<number | null> = new Array(256).fill(null);
        let remainder: number | null = null;

        for (let dividend = 0; dividend <= 255; dividend++) {
            remainder = dividend << 8;

            for (let bit = 8; bit >= 1; bit--) {
                if ((remainder & (0x01 << 15)) === 0x8000) {
                    remainder = ((remainder << 1) ^ polynomial) & 0xffff;
                } else {
                    remainder = (remainder << 1) & 0xffff;
                }
            }

            retTable[dividend] = remainder;
        }

        return retTable;
    }

    parseHeader(): void {
        if (!this.mBytes) return;

        this.mPayloadLength = this.mBytes[1];
        this.mPacketNumber = new DataView(this.mBytes.buffer.slice(2, 4)).getUint16(0, true);
        this.mPacketType = PACKET_TYPE.from(this.mBytes[4]);
        this.rxCRCBytes = this.mBytes.slice(6, 8);
        this.rxCRC = new DataView(this.rxCRCBytes.buffer).getUint16(0, true);

        this.mBytes[6] = 0x00;
        this.mBytes[7] = 0x00;

        this.calcCRC = this.calcCRC16(this.mBytes, 0x00);
        const calcBytes = new Uint8Array(new Uint16Array([this.calcCRC]).buffer);

        this.validCRC = this.calcCRC === this.rxCRC;
    }

    calcCRC16(data: Uint8Array, finalXorVal: number): number {
        let remainder = 0x00;

        for (const byte of data) {
            let datum = (byte & 0xff) ^ ((remainder & 0xffff) >> 8);
            remainder = ((Packet.crcTable[(datum + 256) % 256] || 0) ^ ((remainder << 8) & 0xffff)) & 0xffff;
        }

        return (remainder ^ finalXorVal) & 0xffff;
    }

    getBytesWithRxCRC(): Uint8Array {
        if (!this.mBytes || !this.rxCRCBytes) throw new Error("mBytes or rxCRCBytes is null.");

        const nbytes = this.mBytes.slice();
        nbytes.set(this.rxCRCBytes, 6);

        return nbytes;
    }

    packetTypeAsString(): string {
        switch(this.mPacketType) {
            case PACKET_TYPE.COMMAND: return "Command"
            case PACKET_TYPE.RESPONSE: return "Response"
            case PACKET_TYPE.APP_COMMAND: return "App Command"
            case PACKET_TYPE.APP_RESPONSE: return "App Response"
            case PACKET_TYPE.STATUS: return "Status"
            case PACKET_TYPE.APP_STATUS: return "App Status"
            case PACKET_TYPE.OUTPUT_SET: return "Output Set"
            default: return "Unknown"
        }
    }

    packetSubTypeAsString(): string {
        return ""
    }

    packetPayloadAsString(): string {
        return ""
    }

    calcBytes(): boolean {
        if(this.mPayloadLength === null) {
            console.warn("no payload length set, unable to calc bytes");
            return false;
        }
        if(this.mPacketType === null) {
            console.warn("no packet type set, unable to calc bytes");
            return false;
        }
        if(this.pktSubType === null) {
            console.warn("no packet sub type set, unable to calc bytes");
            return false;
        }
        const mBytes = new Uint8Array((2 + this.mPayloadLength) * 4).fill(0x00);
        
        mBytes[0] = 0xCC;
        mBytes[1] = this.mPayloadLength;

        const packetNumberBytes = Uint16Bytes(this.mPacketNumber!);
        mBytes.set(packetNumberBytes, 2);

        mBytes[4] = this.mPacketType!;
        mBytes[5] = this.pktSubType!;

        if(this.payloadBytes === null) {
            console.warn("no payload bytes set, unable to calc bytes");
            return false;
        }
        mBytes.set(this.payloadBytes, 8)

        const crc = this.calcCRC16(mBytes, 0x00);
        const crcBytes = Uint16Bytes(crc);
        mBytes.set(crcBytes, 6);

        this.mBytes = mBytes
        return true;
    }
}

class Output {
    mIndex: number;
    mType: DATA_TYPE | null;
    mLength: number;
    mName: string;

    constructor(index: number, type: number, length: number) {
        this.mIndex = index;
        this.mType = DATA_TYPE.from(type);
        this.mLength = length;
        this.mName = "";
    }
}

class Publisher {
    pubId: number;
    pubIdx: number;
    numOutputs: number;
    accessTypes: number;
    expectedPeriod: number;
    name: string | null = null;
    outputs: Output[] | null = null;

    constructor(pubId: number, pubIdx: number, numOutputs: number, accessTypes: number, expectedPeriod: number) {
        this.pubId = pubId;
        this.pubIdx = pubIdx;
        this.numOutputs = numOutputs;
        this.accessTypes = accessTypes;
        this.expectedPeriod = expectedPeriod;
    }
}

export abstract class Packet_Abstract extends Packet {
    abstract getLoggingString(): string;
}

export enum PACKET_TYPE {
    OUTPUT_SET = 0x00,
    DATA_BLOCK_REQ = 0x01,
    DATA_BLOCK_RESP = 0x02,
    STATUS = 0x03,
    COMMAND = 0x04,
    RESPONSE = 0x05,
    APP_STATUS = 0x06,
    APP_COMMAND = 0x07,
    APP_RESPONSE = 0x08,
    NA = 0x0F,
}

export namespace PACKET_TYPE {
    export function from(b: number): PACKET_TYPE | null {
        for (const key in PACKET_TYPE) {
            if (PACKET_TYPE[key as keyof typeof PACKET_TYPE] === b) {
                return PACKET_TYPE[key as keyof typeof PACKET_TYPE] as PACKET_TYPE;
            }
        }
        return null;
    }
}

enum SYSTEM_POWER {
    NA = 0x00,
    NORMAL = 0x01,
    SLEEP = 0x02,
    DEEP_SLEEP = 0x03,
    OFF = 0x04,
}

namespace SYSTEM_POWER {
    export function from(b: number): SYSTEM_POWER | null {
        for (const key in SYSTEM_POWER) {
            if (SYSTEM_POWER[key as keyof typeof SYSTEM_POWER] === b) {
                return SYSTEM_POWER[key as keyof typeof SYSTEM_POWER] as SYSTEM_POWER;
            }
        }
        return null;
    }
}

enum PARSING_SUCCESS {
    SUCCESS,
    INVALID_CRC,
    NA,
}
